import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Typography, Box } from '@mui/material';
import { Form, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { PageHeader } from 'shared/ui/PageHeader';
import { ColorPalette } from 'shared/consts/colorPalette';
import { postDefaultImport } from 'entities/Files/api';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { useCustomModelStore } from 'widgets/customModel/state';
import { FormSwitch } from 'shared/ui/Switch/FormSwitch';
import { Paper } from 'shared/ui/Paper';
import { CategoriesList } from 'widgets/startRecognition/consts/CategoryList';
import { CategoryTypeEnum } from 'entities/Files/types';
import { InputField } from 'shared/ui/InputField';
import { Button } from 'shared/ui/Button';
import { useAuthStore } from 'processes/auth/model/state';

type ModelItem = {
	name: string;
	category: CategoryTypeEnum;
	type?: string;
};

const modelValueShape: Record<CategoryTypeEnum, yup.StringSchema> = Object.values(CategoryTypeEnum).reduce(
	(acc, key) => {
		acc[key] = yup.string().matches(/^\d+(-\d+)?(,\d+(-\d+)?)*$/, 'files.default_import.validation');
		return acc;
	},
	{} as Record<CategoryTypeEnum, yup.StringSchema>,
);
const importSettingsSchema = yup.object({
	isEnabled: yup.bool().required(),
	models: yup.object().shape(modelValueShape),
});

const ImportSettings: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { customModels, generativeModels } = useCustomModelStore();
	const { company } = useAuthStore();

	const modelsList = useMemo<ModelItem[]>(
		() => CategoriesList.map((item) => ({ name: t(item.label), category: item.id })),
		[customModels, generativeModels],
	);

	const defaultFormModels = useMemo(() => {
		const result = Object.values(CategoryTypeEnum).reduce((acc, key) => {
			acc[key] = '';
			return acc;
		}, {} as Record<CategoryTypeEnum, string>);

		company?.defaultImport?.pagesSettings?.models?.forEach(({ category, pagesRange }) => {
			if ((Object.values(CategoryTypeEnum) as string[]).includes(category)) {
				result[category as CategoryTypeEnum] = pagesRange;
			}
		});
		return result;
	}, [company]);

	const { mutate, isLoading } = useMutation({
		mutationKey: ['post/files/settings'],
		mutationFn: postDefaultImport,
		onSuccess: async () => {
			await queryClient.refetchQueries({ queryKey: ['validate-token'] });
			enqueueSnackbar({ message: t('shared_texts.success_update'), variant: 'success' });
			navigate(-1);
		},
		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({ message: errorMessage, variant: 'error' });
		},
	});

	const handleSubmit = (data: yup.InferType<typeof importSettingsSchema>) => {
		const models = Object.entries(data.models)
			.map(([category, pagesRange]) => ({
				category: category as CategoryTypeEnum,
				pagesRange,
			}))
			.filter((item) => item.pagesRange);
		mutate({ pagesSettings: { isEnabled: data.isEnabled, models } });
	};

	return (
		<>
			<PageHeader
				title={t('files.default_import.title')}
				description={t('files.default_import.description')}
			/>

			<Box
				sx={{
					'@media screen and (min-width: 1441px)': {
						width: '50%',
					},
				}}
			>
				<Formik
					initialValues={{
						isEnabled: company?.defaultImport?.pagesSettings?.isEnabled || false,
						models: defaultFormModels,
					}}
					validationSchema={importSettingsSchema}
					onSubmit={handleSubmit}
				>
					{({ values }) => (
						<Form>
							<Paper sx={{ width: '100%', padding: 4 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<FormSwitch name="isEnabled" sx={{ marginRight: '10px' }} />
									<Typography variant="body1" color={ColorPalette.astronaut}>
										{t('files.default_import.process')}
									</Typography>
								</Box>
							</Paper>

							{values.isEnabled && (
								<>
									<Typography
										variant={'subtitle1'}
										color={ColorPalette.baliHai}
										sx={{ marginY: 4 }}
									>
										{t('files.default_import.description_2')}
									</Typography>
									<Paper
										sx={{
											'width': '100%',
											'paddingX': 4,
											'paddingY': 2,
											'& > div:not(:last-child)': {
												borderBottom: `1px solid ${ColorPalette.periwinkle}`,
											},
										}}
									>
										{modelsList.map((item, i) => (
											<Box
												key={`${item.category}_${i}`}
												sx={{
													paddingY: 2,
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
												}}
											>
												<Box sx={{ display: 'flex', alignItems: 'center' }}>
													<Box sx={{ display: 'flex', flexDirection: 'column' }}>
														<Typography color={ColorPalette.astronaut}>
															{item.name}
														</Typography>
														<Typography
															variant="caption"
															color={ColorPalette.baliHai}
														>
															{item.type}
														</Typography>
													</Box>
												</Box>
												<InputField
													label={''}
													name={`models.${item.category}`}
													placeholder={'Example: 1-3 , 5'}
													sx={{ width: '50%', margin: '0' }}
													sxError={{ position: 'initial' }}
													required
												/>
											</Box>
										))}
									</Paper>
								</>
							)}
							<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
								<Button
									loading={isLoading}
									onClick={() => navigate(-1)}
									size={'small'}
									variant="outlined"
								>
									{t('shared_texts.cancel')}
								</Button>
								<Button disabled={isLoading} size={'small'} type={'submit'}>
									{t('shared_texts.save')}
								</Button>
							</Box>
						</Form>
					)}
				</Formik>
			</Box>
		</>
	);
};

export default ImportSettings;
